import { APIRoutes } from '@/services/api.routes';
import type { SubscriptionPriceProduct, SubscriptionProductType, SubscriptionType } from '@/models';

/**
 * `GET /subscriptions/products` ist die einzige Quelle für die existierenden Subscription-
 * Typen, ihre Produktfamilien und ihre Anzeigenamen. Die Liste enthält auch Typen ohne
 * Stripe-Produkt (nur manuell vergebbar) — erkennbar an `stripeDataAvailable === false`.
 *
 * Die Antwort ändert sich pro Deployment praktisch nie, deshalb wird sie einmal pro
 * Seitenaufruf geladen und danach aus dem Modul-Cache bedient.
 */
let cachedProducts: SubscriptionPriceProduct[] | null = null;
let pendingLoad: Promise<SubscriptionPriceProduct[]> | null = null;

export async function loadSubscriptionProducts(force = false): Promise<SubscriptionPriceProduct[]> {
  if (!force && cachedProducts !== null) {
    return cachedProducts;
  }
  if (!force && pendingLoad !== null) {
    return pendingLoad;
  }

  const load = APIRoutes.getSubscriptionProducts().then(response => {
    cachedProducts = response.products;
    return response.products;
  });
  pendingLoad = load;

  try {
    return await load;
  } finally {
    if (pendingLoad === load) {
      pendingLoad = null;
    }
  }
}

/** Bereits geladene Produkte ohne Request — leer, solange {@link loadSubscriptionProducts} nicht durchgelaufen ist. */
export function getCachedSubscriptionProducts(): SubscriptionPriceProduct[] {
  return cachedProducts ?? [];
}

export function findSubscriptionProduct(
  products: SubscriptionPriceProduct[],
  type: SubscriptionType
): SubscriptionPriceProduct | undefined {
  return products.find(p => p.type === type);
}

/** Anzeigename inkl. Laufzeit, z.B. "Cashflow (6M)". */
export function getSubscriptionTypeLabel(product: SubscriptionPriceProduct | undefined, type: SubscriptionType): string {
  if (product === undefined) {
    return type;
  }
  if (product.intervalMonths > 0) {
    return `${product.localName} (${product.intervalMonths}M)`;
  }
  return product.localName;
}

/** Abrechnungszeitraum als Klartext, z.B. "Halb-Jährlich". */
export function getSubscriptionPeriodLabel(product: SubscriptionPriceProduct | undefined): string {
  if (product === undefined) {
    return '(?)';
  }
  if (product.isOneTime) {
    return 'One-Time';
  }
  switch (product.intervalMonths) {
    case 0:  return '(?)';
    case 1:  return 'Monatlich';
    case 6:  return 'Halb-Jährlich';
    case 12: return 'Jährlich';
    default: return `${product.intervalMonths} Monate`;
  }
}

export interface SubscriptionProductTypeInfo {
  productType: SubscriptionProductType;
  label: string;
}

/** Alle vorkommenden Produktfamilien mit Anzeigename, in der Reihenfolge des Backends. */
export function collectProductTypes(products: SubscriptionPriceProduct[]): SubscriptionProductTypeInfo[] {
  const result: SubscriptionProductTypeInfo[] = [];
  for (const product of products) {
    if (product.productType === null) continue;
    if (result.some(e => e.productType === product.productType)) continue;
    result.push({ productType: product.productType, label: product.productTypeName });
  }
  return result;
}

/** Ein Produkt ohne hinterlegte Stripe-IDs kann nicht gekauft werden — ein Checkout darauf schlägt fehl. */
export function hasStripeCheckout(product: SubscriptionPriceProduct | undefined): boolean {
  return product !== undefined && product.stripeProductId !== '' && product.stripePriceId !== '';
}

/**
 * Produktfamilien, deren Stripe-Checkout auf eine externe Danke-Seite zurückleitet (Backend
 * `successURLOverride`, nicht im Produkt-DTO enthalten — daher hier gespiegelt). Nach so einem
 * Kauf kommt der Kunde nicht auf /checkout/success zurück; ein verketteter Checkout kann danach
 * nicht weiterlaufen. Muss mit `models/subscriptionProduct.go` im Backend synchron gehalten werden.
 */
const EXTERNAL_SUCCESS_PRODUCT_TYPES: SubscriptionProductType[] = ['WORKSHOP_01'];

export function hasExternalSuccessPage(product: SubscriptionPriceProduct | undefined): boolean {
  return product !== undefined && product.productType !== null && EXTERNAL_SUCCESS_PRODUCT_TYPES.includes(product.productType);
}
